import React, {Component} from 'react';
import {createPortal} from 'react-dom';
import {css} from 'styled-components';

import {globalDocument} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {
  buildInteractionIdProps,
  InteractionComponentProps,
} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {LightUserThemeProvider, styled} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {StyledActionButton} from './bannerAction';
import {StyledCloseButton} from './bannerCloseAction';
import {BannerPrioritiesEnum, BannersApiContextProps, withBannersApi} from './bannersContext';

/*
 * Props.
 */

interface BannerProps extends InteractionComponentProps {
  priority: BannerPrioritiesEnum;
  bannerType: BannerTypesEnum;
  color?: string;
}

export enum BannerTypesEnum {
  INLINE = 'inline',
  MULTILINE = 'multiline',
}

/*
 * Style.
 */

interface BannerStyleProps {
  bannerType: BannerTypesEnum;
  $color?: string;
}

const StyledBanner = styled.div<BannerStyleProps>`
  position: relative;
  overflow: hidden;

  display: grid;
  grid-template-columns: auto 1fr max-content;

  border-radius: 18px;
  background-color: ${(p) => p.$color || p.theme.bannerBackground};
  ${(p) => boxShadow(p.theme, `0 2px 15px ${p.theme.alphas.black50}`)};

  pointer-events: all;

  ${(p) => addInlineStyles(p)}
  ${(p) => addMultilineStyles(p)}
`;

function addInlineStyles(p: BannerStyleProps) {
  if (p.bannerType === BannerTypesEnum.INLINE) {
    return css`
      grid-template-areas: 'icon content action';
      height: 34px;
      padding: 0 12px 0 9px;
      grid-column-gap: 8px;
      align-items: center;
    `;
  }
  return '';
}

function addMultilineStyles(p: BannerStyleProps) {
  if (p.bannerType === BannerTypesEnum.MULTILINE) {
    return css`
      padding: 11px 13px 0 12px;
      grid-template-areas:
        'icon content action'
        '. content .'
        '. footer .';
      grid-column-gap: 11px;

      ${StyledCloseButton} {
        position: relative;
        top: -5px;
        right: -5px;
      }

      ${StyledActionButton} {
        font-size: ${fontSizes.verySmall};
      }
    `;
  }
  return '';
}

/*
 * Component.
 */

let bannerId = 0;

class BannerComponent extends Component<BannerProps & BannersApiContextProps> {
  constructor(props: BannerProps & BannersApiContextProps) {
    super(props);
    this.id = ++bannerId;
    // TODO: Should this component receive a document prop, rather than using the global document?
    this.node = globalDocument.createElement('div');
  }

  private readonly id: number;
  private readonly node: HTMLElement;
  private bannerUpdater?: Handler;

  /*
   * Lifecycle.
   */

  componentDidMount() {
    // If a context wasn't provided, nothing to do.
    const {createBanner, priority} = this.props;
    if (!createBanner) {
      return;
    }

    // Otherwise, register our banner.
    this.bannerUpdater = createBanner({
      id: this.id,
      node: this.node,
      priority,
    });
  }

  componentWillUnmount() {
    if (this.bannerUpdater) {
      this.bannerUpdater();
    }
  }

  /*
   * Render.
   */

  render() {
    // If no context was provided, render directly.
    if (!this.props.createBanner) {
      return this.renderBanner();
    }

    // Otherwise, render in a portal.
    return createPortal(this.renderBanner(), this.node);
  }

  private renderBanner() {
    const contentRender = (
      <StyledBanner
        bannerType={this.props.bannerType}
        {...buildInteractionIdProps(this.props.interactionId ?? `banner-${this.id}`)}
        $color={this.props.color}
      >
        {this.props.children}
      </StyledBanner>
    );

    // If we have no color, render directly.
    if (!this.props.color) {
      return contentRender;
    }

    // Otherwise, overwrite the theme.
    return <LightUserThemeProvider>{contentRender}</LightUserThemeProvider>;
  }
}

export const Banner = withBannersApi(BannerComponent);
